import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppToast from "../../../components/appToast";
import { listAction as categoryListAction } from "../../../network/store/action/CategoryResponseAction";
import { createUpdateAction } from "../../../network/store/action/ProductResponseAction";
import { listAction as subCategoryListAction } from "../../../network/store/action/SubCategoryResponseAction";
import {
  getAdminNutrition,
  getCombo,
  lookupNutrition,
  previewScore,
  removeCombo,
  saveCombo,
  saveNutrition,
} from "../../../network/service/NutritionService";
import { hasPermission, platformUser, PRODUCT_UPDATE } from "../../../network/service/PlatformAuth";
import { formUpdateData } from "../../../redux/slice/formUpdateSlice";
import { updateMessage } from "../../../redux/slice/toastSlice";
import ComboBuilder from "../combo/combo-builder";
import AutoFillBox from "../nutrition/auto-fill-box";
import CustomerPreview from "../nutrition/customer-preview";
import GradeCard from "../nutrition/grade-card";
import NutritionFields from "../nutrition/nutrition-fields";
import ScoreHistoryPanel from "../nutrition/score-history-panel";
import SourcesPanel from "../nutrition/sources-panel";
import {
  EMPTY,
  fromCandidate,
  fromEstimate,
  fromLegacy,
  fromProfile,
  isKitProduct,
  kitItems,
  kitTotalsBasis,
  legacyMacros,
  macrosValid,
  minimumBasisGrams,
  packWeightFromName,
  per100Values,
  SOURCE_NAMES,
  toPreviewBody,
  toSaveBody,
  valuesImpossibleForBasis,
  withEdits,
} from "../nutrition/nutrition-model";
import IngredientEstimate from "../nutrition/ingredient-estimate";
import PlatformSignInNotice from "../nutrition/platform-sign-in-notice";
import { getPlatformProduct, updateProductShopDetails } from "../../../network/service/ShopService";
import ShopDetailsFields, { EMPTY_SHOP_DETAILS, fromPlatformProduct, toShopDetailsBody } from "./shop-details-fields";
import { gradeName } from "../nutrition/grade-names";
import "../nutrition/nutrition-panel.css";
import "./product-editor.css";

/** A name match at or above this fills the form by itself (server: AUTO_APPLY_CONFIDENCE). */
const AUTO_FILL_CONFIDENCE = 0.8;
const ADMIN_SOURCES = ["MANUAL", "LAB_TESTED", "PRODUCT_LABEL"];

const isConfident = (c) => c && c.identity?.ok !== false && (c.byBarcode || c.confidence >= AUTO_FILL_CONFIDENCE);

/** What the store category says beats the database: a juice is a drink, a vegetable juice a vegetable. */
const withHint = (f, hint) =>
  hint
    ? {
        ...f,
        isBeverage: f.isBeverage || hint.isBeverage,
        foodCategory: hint.foodCategory && hint.foodCategory !== "BEVERAGE" ? hint.foodCategory : f.foodCategory,
      }
    : f;
const validBarcode = (b) => /^\d{8,14}$/.test(b);

/**
 * Add / update a product with its full nutrition label and live health
 * grade. Nutrition is found automatically in Open Food Facts / USDA / FatSecret from
 * the name or barcode; saving writes the product to the admin system and
 * the nutrition to the nutrition service.
 */
function ProductEditor({ mode }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editing = mode === "edit";
  const product = useSelector(formUpdateData).product;

  const [details, setDetails] = useState({
    name: editing ? product?.product_name ?? "" : "",
    description: editing ? product?.description ?? "" : "",
    categoryId: editing ? String(product?.category_id ?? "") : "",
    subCategoryId: editing && Number(product?.subcategory_id) > 0 ? String(product.subcategory_id) : "",
  });
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const user = platformUser();
  const canEdit = !!user && hasPermission(PRODUCT_UPDATE);

  const [form, setForm] = useState(editing ? fromLegacy(product) : EMPTY);
  const [stored, setStored] = useState({ loading: editing, exists: false, profile: null, facts: null });
  const [report, setReport] = useState(null);
  const [lookup, setLookup] = useState({ state: "idle", candidates: [], warnings: [] });
  // The form just before a database entry filled it, so "Don't use this" can put it back.
  const beforeFill = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sideView, setSideView] = useState("customer");
  // Kit weight filled in from the description, shown until the admin edits nutrition.
  const [kitApplied, setKitApplied] = useState(null);
  const [shopDetails, setShopDetails] = useState(EMPTY_SHOP_DETAILS);
  // Combos / weekly packs: nutrition is added up from their contents, not typed in.
  // enabled: the product is a pack; data: its saved contents (GET combos/:id).
  const [combo, setCombo] = useState({ enabled: false, data: null });
  // The contents as being edited (reported by the builder), saved with the product.
  const [comboDraft, setComboDraft] = useState(null);
  const savedCombo = !!combo.data?.combo;
  const shopDirty = useRef(false);

  // Nutrition typed by the admin is never overwritten by a later automatic lookup.
  const touched = useRef(false);
  const dirty = useRef(false);

  // ---- categories (admin system) ----
  useEffect(() => {
    dispatch(categoryListAction({}, dispatch)).then((r) => r?.result && setCategories(r.result));
  }, [dispatch]);

  useEffect(() => {
    setSubCategories([]);
    if (!details.categoryId) return;
    dispatch(subCategoryListAction({ secId: details.categoryId }, dispatch)).then(
      (r) => r?.result && setSubCategories(r.result),
    );
  }, [details.categoryId, dispatch]);

  // ---- automatic lookup ----
  const runLookup = useCallback(
    async ({ manual = false, name = details.name, barcode = form.barcode } = {}) => {
      if (!canEdit || (!name.trim() && !validBarcode(barcode))) return;
      // A kit of dishes is not in any food database ("Breakfast Pack 1" -> "Breakfast tart").
      if (!manual && isKitProduct(name, details.description)) return;
      setLookup({ state: "busy", candidates: [], warnings: [] });
      try {
        const r = await lookupNutrition({
          name: name.trim() || undefined,
          barcode: validBarcode(barcode) ? barcode : undefined,
          categoryId: details.categoryId ? Number(details.categoryId) : undefined,
          subCategoryId: details.subCategoryId ? Number(details.subCategoryId) : undefined,
          productForm: form.productForm || undefined,
        });
        // Only an entry confirmed to be this exact food fills the form by itself.
        const best = r.best;
        const fill = isConfident(best) && (manual || !touched.current);
        if (fill) {
          dirty.current = true;
          // With the gaps other entries filled and the values worked out from the rest.
          setForm((f) => {
            beforeFill.current = beforeFill.current ?? f;
            return withHint(fromCandidate(f, best, r.complete), r.hint);
          });
        }
        setLookup({
          state: "done",
          filled: fill ? best : null,
          complete: fill ? r.complete : null,
          candidates: r.candidates,
          steps: r.steps ?? [],
          warnings: r.warnings,
        });
      } catch (e) {
        setLookup({ state: "error", candidates: [], warnings: [], error: e.message });
      }
    },
    [canEdit, details.name, details.description, details.categoryId, details.subCategoryId, form.barcode, form.productForm],
  );

  // Update: load what is stored; look up automatically when it is missing or incomplete.
  useEffect(() => {
    if (!editing || !canEdit || !product?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [data, comboData] = await Promise.all([
          getAdminNutrition(product.id),
          // Not on the platform yet: no combo either.
          getCombo(product.id).catch(() => null),
        ]);
        if (cancelled) return;
        setStored({ loading: false, exists: !!data.product, profile: data.profile, facts: data.ingredientFacts });
        if (data.profile) setForm(fromProfile(data.profile, data.ingredientFacts));
        setReport(data.health);
        if (comboData?.combo) {
          setCombo({ enabled: true, data: comboData });
          return;
        }

        // Look up when nutrition is missing or not gradable, but never over
        // values an admin entered or checked.
        const adminEntered = data.profile && (data.profile.isVerified || ADMIN_SOURCES.includes(data.profile.source));
        const incomplete = !data.profile || data.health?.grade === "UNRATED";
        if (!adminEntered && incomplete) {
          runLookup({ name: product.product_name ?? "", barcode: data.profile?.barcode ?? "" });
        }
      } catch (e) {
        if (!cancelled) {
          setStored((s) => ({ ...s, loading: false }));
          setError(e.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once per product / sign-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, canEdit, product?.id]);

  // After a source is unlinked the server may merge the values again; show
  // them unless the admin has unsaved changes.
  const reloadNutrition = async () => {
    try {
      const data = await getAdminNutrition(product.id);
      setStored((s) => ({ ...s, profile: data.profile, facts: data.ingredientFacts }));
      if (data.profile && !dirty.current) {
        setForm(fromProfile(data.profile, data.ingredientFacts));
        setReport(data.health);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  // Update: shop details (local name, attributes...) from the platform product.
  useEffect(() => {
    if (!editing || !product?.id) return;
    let cancelled = false;
    getPlatformProduct(product.id)
      .then((p) => !cancelled && !shopDirty.current && setShopDetails(fromPlatformProduct(p)))
      .catch(() => {
        // Not on the platform yet (404) or commerce unreachable: start empty.
      });
    return () => {
      cancelled = true;
    };
  }, [editing, product?.id]);

  // Add: look up as the admin types the name.
  useEffect(() => {
    // A pack's nutrition comes from its contents, not a food database.
    if (editing || !canEdit || combo.enabled || details.name.trim().length < 3 || touched.current) return;
    const timer = setTimeout(() => runLookup({ name: details.name }), 1200);
    return () => clearTimeout(timer);
    // The store category says whether it is sold loose or packed, which changes the match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.name, details.categoryId, details.subCategoryId, editing, canEdit, combo.enabled]);

  // ---- live grade ----
  useEffect(() => {
    if (!canEdit || !macrosValid(form)) return;
    // Values no 100 g can have would grade as e.g. "112 g protein per 100 g".
    if (valuesImpossibleForBasis(form)) {
      setReport(null);
      return;
    }
    if (!dirty.current) return;
    const timer = setTimeout(async () => {
      try {
        setReport(await previewScore(toPreviewBody(form)));
      } catch {
        // The last grade stays visible; saving reports real errors.
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form, canEdit]);

  const setNutrition = (updates) => {
    touched.current = true;
    dirty.current = true;
    setKitApplied(null);
    setForm((f) => withEdits(f, updates));
  };

  const useEstimate = (estimate) => {
    touched.current = true;
    dirty.current = true;
    setForm((f) => fromEstimate(f, estimate));
    setLookup((l) => ({ ...l, filled: null, complete: null }));
  };

  // ---- whole-pack totals ----
  // Values for more than 100 g (a whole kit or pack) are kept as entered with
  // that weight as "Values are per"; the grade is then worked out per 100 g.
  // Kits: the weight of the contents in the description ("Chicken Breast
  // 500g , Eggs 1 Tray"); other packs: the weight in the name ("... 200g").
  const looksLikePackTotals = valuesImpossibleForBasis(form);
  const kitWeight = looksLikePackTotals ? kitTotalsBasis(form, details.description) : null;
  const nameWeight = looksLikePackTotals && !kitWeight ? packWeightFromName(form, details.name) : null;
  const applyKitWeight = (grams) => setNutrition({ basisGrams: String(grams), servingGrams: String(grams) });
  // A pack's serving stays as entered; for a kit, one serving is the whole kit.
  const applyPackWeight = (grams) =>
    setNutrition({ basisGrams: String(grams), servingGrams: form.servingGrams || String(grams) });
  const [kitWeightInput, setKitWeightInput] = useState("");
  const minWeight = looksLikePackTotals ? minimumBasisGrams(form) : 0;
  const weightTooLow = Number(kitWeightInput) > 0 && Number(kitWeightInput) < minWeight;
  const wholePackGrams = !looksLikePackTotals && Number(form.basisGrams) > 100 ? Number(form.basisGrams) : null;
  const per100 = wholePackGrams ? per100Values(form) : null;

  const isKit = isKitProduct(details.name, details.description);
  const contents = looksLikePackTotals && !kitWeight ? kitItems(details.description) : [];
  const unweighedItems = contents.filter((i) => i.grams === null);
  const kitKnownGrams = Math.round(contents.reduce((sum, i) => sum + (i.grams ?? 0), 0));

  useEffect(() => {
    const grams = kitWeight ?? nameWeight;
    if (!canEdit || stored.loading || touched.current || !grams) return;
    dirty.current = true;
    setForm((f) => ({ ...f, basisGrams: String(grams), servingGrams: kitWeight ? String(grams) : f.servingGrams || String(grams) }));
    setKitApplied({ grams, from: kitWeight ? "kit" : "name" });
  }, [kitWeight, nameWeight, canEdit, stored.loading]);

  const useCandidate = (c) => {
    touched.current = true;
    dirty.current = true;
    setForm((f) => {
      beforeFill.current = beforeFill.current ?? f;
      return fromCandidate(f, c);
    });
    setLookup((l) => ({ ...l, filled: c, complete: null }));
  };

  /** Not this product: back to the values from before the fill; pick another match or type them in. */
  const discardFill = () => {
    if (beforeFill.current) setForm(beforeFill.current);
    beforeFill.current = null;
    setLookup((l) => ({ ...l, filled: null, complete: null }));
  };

  // ---- single product or pack ----
  const setKind = (isCombo) => {
    if (isCombo === combo.enabled) return;
    if (!isCombo && savedCombo) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        "Stop adding up this product's nutrition from its contents? When you save, the contents are removed and the last calculated values stay, to edit by hand.",
      );
      if (!ok) return;
    }
    setCombo((c) => ({ ...c, enabled: isCombo }));
    setError("");
  };

  // ---- save ----
  const onSave = async () => {
    setError("");
    if (!details.name.trim() || !details.categoryId) {
      setError("Enter the product name and choose a category.");
      return;
    }
    if (canEdit && combo.enabled && !comboDraft?.complete) {
      setError(
        comboDraft?.empty
          ? "Add the products in this pack: search for them under “Pack contents”."
          : "Set how many of each product (and the grams where you chose “Enter grams…”).",
      );
      return;
    }
    if (canEdit && !combo.enabled && valuesImpossibleForBasis(form)) {
      setError(
        `Nothing was saved: these values can't be per ${form.basisGrams || 100} g. Enter the weight of the whole pack or kit ` +
          `(at least ${minimumBasisGrams(form)} g) in the nutrition section, or the values per 100 g.`,
      );
      return;
    }
    if (canEdit && !combo.enabled && !macrosValid(form)) {
      setError("Energy, protein, carbohydrates and fat are required. Try “Find nutrition automatically”.");
      return;
    }

    setSaving(true);
    try {
      // 1. Product in the admin system (still used by the current apps).
      // The admin API resets any macro left out to "0", so always send them:
      // from the nutrition form, or unchanged from the product.
      const packMacros = combo.enabled ? comboDraft?.preview?.nutrition?.per100g : null;
      const macros = packMacros
        ? legacyMacros({ ...packMacros, basisGrams: "100" })
        : !combo.enabled && macrosValid(form)
          ? legacyMacros(form)
          : editing
            ? originalMacros(product)
            : legacyMacros(form);
      const legacy = {
        ...(editing && { id: product.id }),
        name: details.name.trim(),
        description: details.description,
        categoryId: details.categoryId,
        subCategoryId: details.subCategoryId,
        ...macros,
        summary:
          `There are ${macros.cal_grm} calories in 100 grams of <b>${details.name.trim()}</b> 
  ` +
          `Calories Breakdown: <b>${macros.fat_percent}%</b> fat, <b>${macros.carbs_percent}%</b> carbs, ` +
          `<b>${macros.protein_percent}%</b> protein.`,
      };
      const response = await dispatch(createUpdateAction(legacy, dispatch));
      const productId = editing ? product.id : response?.result?.id;
      if (!response?.status || !productId) {
        // The admin API puts the reason per field in `result` (e.g. { name: "Name not available. Try another." })
        // and only a generic "Required fields are missing" in `message`.
        const fieldErrors =
          response?.result && typeof response.result === "object"
            ? Object.values(response.result).filter((v) => typeof v === "string")
            : [];
        throw new Error(
          fieldErrors.join(" ") || response?.message || "The admin system did not save the product (name may already exist).",
        );
      }

      // 2. Full nutrition label + grade (a pack: its contents, added up by the server).
      const basics = {
        name: details.name.trim(),
        categoryId: Number(details.categoryId),
        ...(Number(details.subCategoryId) > 0 && { subCategoryId: Number(details.subCategoryId) }),
        ...(details.description && { description: details.description }),
      };
      let grade = null;
      let pending = null;
      if (canEdit && combo.enabled) {
        const saved = await saveCombo(productId, {
          items: comboDraft.items,
          ...(comboDraft.days && { days: comboDraft.days }),
          ...(comboDraft.people && { people: comboDraft.people }),
          product: basics,
        }).catch((e) => {
          throw new Error(`Product saved, but its pack contents were not: ${e.message}`);
        });
        grade = saved.nutrition?.health ?? null;
        pending = saved.combo?.problem ?? null;
      } else if (canEdit) {
        if (savedCombo) {
          // Switched back to a single product: hand-edited from now on.
          await removeCombo(productId);
        }
        const saved = await saveNutrition(productId, toSaveBody(form, basics)).catch((e) => {
          throw new Error(`Product saved, but nutrition was not: ${e.message}`);
        });
        grade = saved.health;
      }

      // 3. Shop details on the platform product (created by the nutrition save).
      if (canEdit && shopDirty.current) {
        await updateProductShopDetails(productId, toShopDetailsBody(shopDetails)).catch((e) => {
          throw new Error(`Product and nutrition saved, but shop details were not: ${e.message}`);
        });
      }

      dispatch(
        updateMessage({
          display: true,
          message: pending
            ? `Saved. Nutrition is added up once this is fixed: ${pending}`
            : grade
            ? `Saved. Health grade: ${grade.grade === "UNRATED" ? "not yet rated" : `${gradeName(grade.grade)} (${grade.eatProteinScore}/100)`}`
            : editing
              ? "Product updated."
              : "Product created.",
        }),
      );
      navigate(-1);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const needsReview = stored.profile?.needsReview && !touched.current;

  // A pack shows what its contents add up to, live as they are edited.
  const packNutrition = combo.enabled ? comboDraft?.preview?.nutrition ?? null : null;
  const shownReport = combo.enabled ? packNutrition?.health ?? null : report;
  const shownForm = packNutrition
    ? {
        ...fromProfile({ ...packNutrition.total, isVerified: packNutrition.verified }),
        source: "COMBO_CONTENTS",
      }
    : form;

  return (
    <>
      <AppToast />
      <main className="container-fluid dashboard pe-page">
        <div className="pe-header">
          <h3 className="dashboard-title mb-0">{editing ? "Update product" : "Add product"}</h3>
          {shownReport && (
            <span className="pe-grade-chip" style={{ background: shownReport.color }}>
              {shownReport.eatProteinScore !== null ? `${gradeName(shownReport.grade)} · ${shownReport.eatProteinScore}` : "Not yet rated"}
            </span>
          )}
        </div>

        <div className="pe-layout">
          <div className="pe-main">
            <section className="pe-card">
              <h5>Product details</h5>
              {canEdit && !stored.loading && (
                <div className="pe-kind" role="radiogroup" aria-label="Product type">
                  {[
                    [false, "Single product", "Nutrition from its label or a food database"],
                    [true, "Combo / weekly or monthly pack", "Made of other products; nutrition is added up from them"],
                  ].map(([value, title, hint]) => (
                    <button
                      key={title}
                      type="button"
                      role="radio"
                      aria-checked={combo.enabled === value}
                      className={`pe-kind-option${combo.enabled === value ? " active" : ""}`}
                      onClick={() => setKind(value)}
                    >
                      <strong>{title}</strong>
                      <span>{hint}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="pe-grid">
                <label className="nutri-field">
                  <span>Category *</span>
                  <select
                    className="form-select"
                    value={details.categoryId}
                    onChange={(e) => setDetails({ ...details, categoryId: e.target.value, subCategoryId: "" })}
                  >
                    <option value="">Choose category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="nutri-field">
                  <span>Sub-category</span>
                  <select
                    className="form-select"
                    value={details.subCategoryId}
                    onChange={(e) => setDetails({ ...details, subCategoryId: e.target.value })}
                  >
                    <option value="">Choose sub-category</option>
                    {subCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="nutri-field pe-span2">
                  <span>Product name *</span>
                  <input
                    className="form-control"
                    value={details.name}
                    placeholder={combo.enabled ? "e.g. Weekly Protein Pack" : "e.g. Britannia Good Day Cashew Cookies 200g"}
                    onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  />
                </label>
                <label className="nutri-field pe-span2">
                  <span>Description</span>
                  <input
                    className="form-control"
                    value={details.description}
                    onChange={(e) => setDetails({ ...details, description: e.target.value })}
                  />
                </label>
              </div>
            </section>

            <section className="pe-card">
              <h5>Shop details</h5>
              {!canEdit && <p className="nutri-muted">Needs catalog access on the platform to edit.</p>}
              <ShopDetailsFields
                value={shopDetails}
                disabled={!canEdit}
                onChange={(next) => {
                  shopDirty.current = true;
                  setShopDetails(next);
                }}
              />
            </section>

            {canEdit && !stored.loading && combo.enabled && (
              <section className="pe-card">
                <h5>Pack contents</h5>
                <ComboBuilder
                  key={combo.data?.combo?.updatedAt ?? "new"}
                  productId={editing ? product.id : null}
                  initial={combo.data}
                  onChange={setComboDraft}
                />
              </section>
            )}

            <section className="pe-card">
              <h5>Nutrition &amp; health grade</h5>
              {!user && <PlatformSignInNotice />}
              {user && !canEdit && (
                <div className="nutri-status nutri-status-warn">
                  Your admin role cannot edit nutrition. Ask a super admin for catalog access.
                </div>
              )}
              {canEdit && stored.loading && <p className="nutri-muted">Loading nutrition…</p>}
              {canEdit && !stored.loading && combo.enabled && (
                <div className="nutri-status nutri-status-info">
                  Nothing to type here: the nutrition and grade are added up from the pack contents above (see them on
                  the right), and recalculated by themselves whenever one of those products' nutrition changes. To fix a
                  missing value, use “Fill in missing” next to that product.
                </div>
              )}
              {canEdit && !stored.loading && !combo.enabled && (
                <>
                  {needsReview && (
                    <div className="nutri-status nutri-status-warn">
                      These values were filled automatically from {SOURCE_NAMES[stored.profile.source] ?? "a database"} by
                      product name. Check them and save to confirm.
                    </div>
                  )}
                  {wholePackGrams && per100 && (
                    <div className="nutri-status nutri-status-info">
                      {kitApplied?.grams === wholePackGrams &&
                        (kitApplied.from === "kit"
                          ? `These values are the totals for the whole kit, so "Values are per" was set to ${wholePackGrams} g, the weight of the contents listed in the description. `
                          : `These values are the totals for the whole pack, so "Values are per" was set to ${wholePackGrams} g, the weight in the product name. `)}
                      The values are for the whole {isKit ? "kit" : "pack"} of <b>{wholePackGrams} g</b>.
                      Healthy or not is worked out per 100 g: <b>{per100.calories} kcal</b>, {per100.protein} g protein,{" "}
                      {per100.carbs} g carbs, {per100.fat} g fat
                      {per100.sugar !== null && `, ${per100.sugar} g sugars`} per 100 g. Customers see the grade with
                      both the per-100 g values and the whole {isKit ? "kit" : "pack"}'s weight and totals.
                    </div>
                  )}
                  {looksLikePackTotals && kitWeight && (
                    <div className="nutri-status nutri-status-warn">
                      These values can't be per {form.basisGrams || 100} g (e.g. {form.calories} kcal): they are the
                      totals for the whole kit. Its contents in the description add up to {kitWeight} g.{" "}
                      <button type="button" className="btn btn-sm btn-white-bg" onClick={() => applyKitWeight(kitWeight)}>
                        Use {kitWeight} g
                      </button>
                    </div>
                  )}
                  {looksLikePackTotals && !kitWeight && (
                    <div className="nutri-status nutri-status-error">
                      These values can't be per {form.basisGrams || 100} g (e.g. {form.calories} kcal,{" "}
                      {Math.round(((Number(form.protein) || 0) + (Number(form.carbs) || 0) + (Number(form.fat) || 0)) * 10) / 10} g
                      of protein + carbs + fat). They look like totals for the whole pack or kit.
                      <div className="mt-2">
                        Enter the weight these values are for: they are kept as entered, and healthy or not is
                        worked out per 100 g. Customers see the grade with the complete weight. (Or enter the values
                        per 100 g from the label instead.)
                        {unweighedItems.length > 0 && (
                          <>
                            {" "}No weight could be read for{" "}
                            {unweighedItems.map((i) => `“${i.name}”`).join(", ")}
                            {kitKnownGrams > 0 && ` (the other items add up to ${kitKnownGrams} g)`}. Add a weight to
                            each in the description (e.g. "Chicken Breast 500g, Eggs 1 Tray, Rice 2 Full") or enter
                            the total here:
                          </>
                        )}
                        <div className="nutri-row mt-2">
                          <div className="nutri-unit">
                            <input
                              type="number"
                              min={minWeight}
                              className="form-control"
                              placeholder={`at least ${minWeight}`}
                              value={kitWeightInput}
                              onChange={(e) => setKitWeightInput(e.target.value)}
                            />
                            <em>g</em>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-white-bg"
                            disabled={!(Number(kitWeightInput) >= minWeight)}
                            onClick={() =>
                              (isKit ? applyKitWeight : applyPackWeight)(Math.round(Number(kitWeightInput)))
                            }
                          >
                            Calculate per 100 g
                          </button>
                        </div>
                        {weightTooLow && (
                          <p className="mb-0 mt-1">
                            {kitWeightInput} g is too little for {form.calories} kcal: the pack or kit must weigh at least{" "}
                            {minWeight} g.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <AutoFillBox
                    barcode={form.barcode}
                    onBarcode={(barcode) => {
                      setForm((f) => ({ ...f, barcode }));
                      if (validBarcode(barcode)) runLookup({ manual: true, barcode });
                    }}
                    lookup={lookup}
                    onFind={() => runLookup({ manual: true })}
                    onUse={useCandidate}
                    onDiscard={discardFill}
                  />
                  <NutritionFields
                    form={form}
                    set={setNutrition}
                    detectedAllergens={stored.facts?.detectedAllergens}
                    afterIngredients={
                      <IngredientEstimate
                        ingredients={form.ingredients}
                        product={{
                          name: details.name.trim() || undefined,
                          categoryId: details.categoryId ? Number(details.categoryId) : undefined,
                          subCategoryId: details.subCategoryId ? Number(details.subCategoryId) : undefined,
                        }}
                        onUse={useEstimate}
                      />
                    }
                  />
                </>
              )}
            </section>
          </div>

          <aside className="pe-side">
            <div className="nutri-tabs">
              {[
                ["customer", "Customer view"],
                ["details", "Score details"],
                ...(stored.profile
                  ? [
                      ["sources", "Sources"],
                      ["history", "History"],
                    ]
                  : []),
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`nutri-tab${sideView === value ? " active" : ""}`}
                  onClick={() => setSideView(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {shownReport && sideView === "customer" && (
              <CustomerPreview
                report={shownReport}
                name={details.name.trim()}
                brand={form.brandName}
                imageUrl={lookup.filled?.imageUrl}
                form={shownForm}
              />
            )}
            {shownReport && sideView === "details" && <GradeCard report={shownReport} combo={combo.enabled} />}
            {stored.profile && sideView === "sources" && (
              <SourcesPanel productId={product.id} canEdit={canEdit} onChanged={reloadNutrition} />
            )}
            {stored.profile && sideView === "history" && <ScoreHistoryPanel productId={product.id} />}
            {!shownReport && combo.enabled && !["sources", "history"].includes(sideView) && (
              <p className="nutri-muted">
                What customers will see appears here once the products in the pack are added and each has nutrition.
              </p>
            )}
            {!shownReport && !combo.enabled && looksLikePackTotals && !["sources", "history"].includes(sideView) && (
              <p className="nutri-muted">
                No grade yet: these values can't be per {form.basisGrams || 100} g. Enter the weight of the whole pack
                or kit and the grade appears here, worked out per 100 g.
              </p>
            )}
            {!shownReport && !combo.enabled && !looksLikePackTotals && !["sources", "history"].includes(sideView) && (
              <p className="nutri-muted">
                What customers will see appears here once energy, protein, carbohydrates and fat are filled in.
              </p>
            )}
          </aside>
        </div>

        <div className="pe-actions">
          {error && <span className="text-danger">{error}</span>}
          <button type="button" className="btn btn-white-bg" onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-purple-bg" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save product"}
          </button>
        </div>
      </main>
    </>
  );
}

export default ProductEditor;

/** The product's macros as the admin system has them. */
function originalMacros(p) {
  const v = (x) => (x === undefined || x === null || x === "" ? "0" : String(x));
  return {
    cal_grm: v(p?.cal_grm),
    protein_grm: v(p?.protein_grm),
    carbs_grm: v(p?.carbs_grm),
    fat_grm: v(p?.fat_grm),
    protein_percent: v(p?.protein_percent),
    carbs_percent: v(p?.carbs_percent),
    fat_percent: v(p?.fat_percent),
  };
}
